import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  BlobProvider,
  Image,
} from "@react-pdf/renderer";
import CSLlogo from "@/assets/CSLLogo.png";

interface Service {
  service: string;
  quantity?: number;
  price: number;
}

interface PDFInvoiceProps {
  invoiceData: {
    invoice: {
      number: string;
      date: string;
      unit?: string;
      size?: string;
      services: Service[];
      total: number;
      customField?: string;
    };
  };
  companyData: {
    company: {
      companyName: string;
      companyAddress: string;
      companyCity: string;
      companyState: string;
      companyZip: string;
      companyEmail: string;
      companyLogo: string;
    };
  };
  customerData: {
    customer: {
      name: string;
      address: string;
      city: string;
      state: string;
      zip: string;
      country: string;
      letterId?: string;
    };
  };
}

// Define styles for PDF
const styles = StyleSheet.create({
  page: {
    paddingHorizontal: 30,
    paddingVertical: 40,
    fontSize: 12,
    position: "relative",
  },
  header: {
    flexDirection: "row",
    marginBottom: 2,
  },
  companyInfo: {
    marginBottom: 2,
    marginLeft: 10,
  },
  companyName: {
    marginBottom: 2,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
    fontSize: 14,
  },
  address: {
    marginBottom: 2,
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: "#999",
    marginTop: 10,
    marginBottom: 20,
  },
  dottedDivider: {
    borderBottomWidth: 1,
    borderBottomColor: "#999",
    borderStyle: "dotted",
    marginTop: 20,
    marginBottom: 10,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 30,
    marginBottom: 10,
    paddingHorizontal: 10,
  },
  billTo: {
    marginBottom: 5,
    alignItems: "flex-start",
  },
  invoiceDetails: {
    alignItems: "flex-end",
    marginBottom: 5,
  },
  table: {
    marginTop: 10,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "rgba(151, 210, 252, 0.5)",
    paddingHorizontal: 10,
    paddingVertical: 2.5,
    marginBottom: 10,
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
  },
  tableRow: {
    flexDirection: "row",
    marginBottom: 5,
    paddingHorizontal: 10,
  },
  col1: { width: "50%" },
  col2: { width: "20%", textAlign: "center" },
  col3: { width: "30%", textAlign: "right" },
  total: {
    marginTop: 10,
    fontSize: 16,
    alignItems: "flex-end",
    fontFamily: "Helvetica-Bold",
  },
  invoiceLabel: {
    position: "absolute", // adjust as needed based on your design
    fontSize: 20,
    top: 40,
    right: 40,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
  },
  invoiceNumber: {
    position: "absolute", // adjust as needed based
    fontSize: 18,
    top: 62,
    right: 40,
    color: "#1c4196",
  },
  companyLogo: {
    alignItems: "flex-start",
    width: 120, // Adjust the width as needed
    height: 70, // Adjust the height as needed
    marginBottom: 5, // Space between the logo and the company name
  },
  boldText: {
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
  },
  footer: {
    position: "absolute",
    bottom: 60,
    left: 0,
    right: 0,
    fontSize: 10,
    textAlign: "center",
    color: "#999",
  },
});

const PDFInvoice: React.FC<PDFInvoiceProps> = ({
  invoiceData,
  companyData,
  customerData,
}) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Header with Company Info */}
      <View style={styles.header}>
        <Image source={CSLlogo} style={styles.companyLogo} />
        <View style={styles.companyInfo}>
          <Text style={styles.companyName}>
            {companyData.company.companyName}
          </Text>
          <Text style={styles.address}>
            {companyData.company.companyAddress}
          </Text>
          <Text style={styles.address}>
            {companyData.company.companyCity},{" "}
            {companyData.company.companyState} {companyData.company.companyZip}
          </Text>
          <Text>{companyData.company.companyEmail}</Text>
        </View>
      </View>

      <View style={styles.invoiceLabel}>
        <Text>INVOICE</Text>
      </View>
      <View style={styles.invoiceNumber}>
        <Text>
          {invoiceData.invoice.number}
          {customerData.customer.letterId &&
            `${customerData.customer.letterId}`}
        </Text>
      </View>

      {/* Bill To and Invoice Details */}
      <View style={styles.row}>
        <View style={styles.billTo}>
          <Text style={{ marginBottom: 2 }}>
            <Text style={styles.boldText}>BILL TO:</Text>{" "}
            {customerData.customer.name}
          </Text>
          <Text style={{ marginBottom: 2 }}>
            {customerData.customer.address}
          </Text>
          <Text style={{ marginBottom: 2 }}>
            {customerData.customer.city}, {customerData.customer.state}
          </Text>
          <Text style={{ marginBottom: 2 }}>{customerData.customer.zip}</Text>
        </View>
        <View style={styles.invoiceDetails}>
          <Text style={{ marginBottom: 2 }}>
            <Text style={styles.boldText}>DATE:</Text>{" "}
            {invoiceData.invoice.date}
          </Text>
          {invoiceData.invoice.unit && (
            <Text style={{ marginBottom: 2 }}>
              <Text style={styles.boldText}>UNIT:</Text>{" "}
              {invoiceData.invoice.unit}
            </Text>
          )}
          {invoiceData.invoice.size && (
            <Text style={{ marginBottom: 2 }}>
              <Text style={styles.boldText}>SIZE:</Text>{" "}
              {invoiceData.invoice.size}
            </Text>
          )}
          {invoiceData.invoice.customField && (
            <Text>
              <Text style={styles.boldText}>P.O. #:</Text>{" "}
              {invoiceData.invoice.customField}
            </Text>
          )}
        </View>
      </View>

      <View style={styles.divider} />

      {/* Services Table */}
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.col1}>SERVICE</Text>
          <Text style={styles.col2}>QTY</Text>
          <Text style={styles.col3}>PRICE</Text>
        </View>

        {invoiceData.invoice.services.map((item, index) => {
          const quantity = Number(item.quantity) || 0;
          const price = Number(item.price) || 0;

          return (
            <View key={index} style={styles.tableRow}>
              <Text style={styles.col1}>{item.service || ""}</Text>
              <Text style={styles.col2}>{quantity || ""}</Text>
              <Text style={styles.col3}>
                $
                {price.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Text>
            </View>
          );
        })}
      </View>

      <View style={styles.dottedDivider} />

      {/* Total */}
      <View style={styles.total}>
        <Text>
          Total Due: $
          {invoiceData.invoice.total.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Text>
      </View>

      <View style={styles.footer}>
        <Text>Generated by Talyko</Text>
      </View>
    </Page>
  </Document>
);

export const PDFDownloadButton: React.FC<PDFInvoiceProps> = ({
  invoiceData,
  companyData,
  customerData,
}) => {
  return (
    <BlobProvider
      document={
        <PDFInvoice
          invoiceData={invoiceData}
          companyData={companyData}
          customerData={customerData}
        />
      }
    >
      {({ url, loading, error }) => {
        if (loading) return "Loading document...";
        if (error) return `Error: ${error}`;
        if (!url) return "Failed to generate PDF";

        return (
          <a
            href={url}
            download={`CSL Invoice ${invoiceData.invoice.number}${customerData.customer.letterId}.pdf`}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            Download
          </a>
        );
      }}
    </BlobProvider>
  );
};

export default PDFInvoice;
